"use client";

import "./globals.css";
import { useEffect } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { captureClientError } from "@/lib/posthog-client";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // PostHog's `capture_exceptions` autocapture only listens for
  // `window.onerror` and `unhandledrejection`. React error boundaries
  // intercept the throw before it reaches either, so without an explicit
  // capture here root-layout crashes would be invisible in Error Tracking.
  useEffect(() => {
    captureClientError(error, "app.global-error", {
      digest: error.digest ?? null,
    });
  }, [error]);

  return (
    <html lang="en">
      <body className="bg-background text-foreground min-h-screen font-sans antialiased">
        <div className="flex min-h-screen flex-col items-center justify-center px-4 text-center">
          <div className="bg-destructive/10 text-destructive flex size-12 items-center justify-center rounded-full">
            <AlertTriangle className="size-6" />
          </div>
          <h1 className="mt-4 text-2xl font-semibold tracking-tight">
            Something went wrong
          </h1>
          <p className="text-muted-foreground mt-2 max-w-sm text-sm">
            An unexpected error occurred. Try again — if it keeps happening,
            contact your administrator.
          </p>
          <Button onClick={reset} size="lg" className="mt-6 gap-2 px-4">
            <RefreshCw />
            Try again
          </Button>
        </div>
      </body>
    </html>
  );
}
